import { useState, useEffect, useCallback, useRef } from 'react';
import { formatFranc, formatFrancAdaptive } from '../../lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { leagueAPI, teamAPI } from '@/lib/api';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { TrendingUp, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import RewardCelebration from '@/components/RewardCelebration';
// SANDBOX_LEAGUE_ID must match backend sandbox_league.SANDBOX_LEAGUE_ID.
import { pickDefaultLeague, splitLeagues, SANDBOX_LEAGUE_ID } from '@/lib/leagueSelection';

// 2026-08-15 — remember the last-used league across reloads (per user).
const lastLeagueKey = (uid) => `fws_last_league_${uid || 'anon'}`;

export default function GlobalHeader({ selectedLeagueId, onLeagueChange }) {
  const { user } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [localLeague, setLocalLeague] = useState(selectedLeagueId || '');
  const [team, setTeam] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  const [leagueCode, setLeagueCode] = useState('');
  // 2026-11-01 — the latest App selection (a /league/:id deep link lands
  // after this header mounted); the mount fetch below must honour it
  // instead of the stale mount-time value.
  const selectedRef = useRef(selectedLeagueId);
  selectedRef.current = selectedLeagueId;

  useEffect(() => {
    (async () => {
      try {
        const [lgRes, tmRes] = await Promise.all([
          leagueAPI.list().catch(() => ({ data: { leagues: [] } })),
          teamAPI.myTeams().catch(() => ({ data: { teams: [] } }))
        ]);
        const lgList = lgRes.data.leagues || [];
        const teams = tmRes.data.teams || [];
        setLeagues(lgList);
        setAllTeams(teams);

        // Pick the league to show. Keeps the current selection only if it's in
        // THIS user's visible list; otherwise falls back to the default global
        // (Yearly) league — see pickDefaultLeague(). This prevents the empty
        // selector + "No Team" after a logout→guest / account switch where a
        // stale league id lingers in App state.
        let stored = '';
        try { stored = localStorage.getItem(lastLeagueKey(user?.id)) || ''; } catch { /* ignore */ }
        const wanted = selectedRef.current || localLeague;
        const chosen = pickDefaultLeague(lgList, wanted || stored, user);
        if (chosen) {
          const keepCurrent = chosen.id === wanted;
          // iter_189 CRITICAL — STRICT league match. The old `|| teams[0]`
          // fallback rendered (and let users DRAFT INTO) another league's
          // team while the selector still read the chosen league's name.
          // If the chosen league genuinely has no team but others do, move
          // the SELECTOR to the first team's league so UI and data agree.
          let finalLeague = chosen;
          let matchingTeam = teams.find((t) => t.league_id === chosen.id) || null;
          if (!matchingTeam && !keepCurrent && teams[0]) {
            const fallbackLg = lgList.find((l) => l.id === teams[0].league_id);
            if (fallbackLg) { finalLeague = fallbackLg; matchingTeam = teams[0]; }
          }
          setLocalLeague(finalLeague.id);
          setTeam(matchingTeam);
          setLeagueCode(finalLeague.invite_code || '');
          onLeagueChange?.(finalLeague.id, matchingTeam, finalLeague);
        }
      } catch (e) { /* ignore */ }
    })();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- re-validate on account switch

  const handleLeagueChange = useCallback((leagueId) => {
    setLocalLeague(leagueId);
    try { localStorage.setItem(lastLeagueKey(user?.id), leagueId); } catch { /* ignore */ }
    const lg = leagues.find(l => l.id === leagueId);
    setLeagueCode(lg?.invite_code || '');
    // Optimistic: show the cached team instantly…
    const cached = allTeams.find(t => t.league_id === leagueId) || null;
    setTeam(cached);
    onLeagueChange?.(leagueId, cached, lg);
    // …then ALWAYS revalidate (iter_188 HIGH): the mount-time cache goes
    // stale after any draft, so switching leagues used to hand App a
    // pre-draft/null team and wipe the fresh roster until a full reload.
    (async () => {
      try {
        const res = await teamAPI.myTeams();
        const teams = res.data?.teams || [];
        setAllTeams(teams);
        const fresh = teams.find(t => t.league_id === leagueId) || null;
        setTeam(fresh);
        onLeagueChange?.(leagueId, fresh, lg);
      } catch (e) { /* keep cached */ }
    })();
  }, [allTeams, leagues, onLeagueChange, user?.id]);

  // 2026-11-01 — other surfaces (quarter-recap pop-up) can switch the league.
  useEffect(() => {
    const onSelect = (e) => { if (e?.detail?.leagueId) handleLeagueChange(e.detail.leagueId); };
    window.addEventListener('fws:select-league', onSelect);
    return () => window.removeEventListener('fws:select-league', onSelect);
  }, [handleLeagueChange]);

  // A /league/:id deep link opened after the list loaded → adopt that league.
  useEffect(() => {
    if (selectedLeagueId && selectedLeagueId !== localLeague && leagues.some((l) => l.id === selectedLeagueId)) {
      handleLeagueChange(selectedLeagueId);
    }
  }, [selectedLeagueId]); // eslint-disable-line react-hooks/exhaustive-deps -- only react to App-driven changes

  // 2026-06 (iter_188) — keep the header's team live: any team mutation in
  // the app fires fws:team-refresh; re-pull so team name / NET WORTH / the
  // league-switch cache can never go stale.
  useEffect(() => {
    const onRefresh = async () => {
      try {
        const [res, lgRes] = await Promise.all([teamAPI.myTeams(), leagueAPI.list().catch(() => null)]);
        const teams = res.data?.teams || [];
        setAllTeams(teams);
        setTeam(teams.find(t => t.league_id === localLeague) || null);
        // Going live changes the league's "N competing" count.
        if (lgRes?.data?.leagues?.length) setLeagues(lgRes.data.leagues);
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('fws:team-refresh', onRefresh);
    return () => window.removeEventListener('fws:team-refresh', onRefresh);
  }, [localLeague]);

  // Rankings reports its board total → the pill shows the exact same number.
  useEffect(() => {
    const onTotal = (e) => {
      const { leagueId, total } = e?.detail || {};
      if (!leagueId || typeof total !== 'number') return;
      setLeagues((ls) => ls.map((l) => (l.id === leagueId && !l.ended ? { ...l, competing_count: total } : l)));
    };
    window.addEventListener('fws:competing-total', onTotal);
    return () => window.removeEventListener('fws:competing-total', onTotal);
  }, []);

  // 2026-09-13 — "Today" is the DAILY counter (reset 00:00 UTC by
  // daily_reset.py); it used to show weekly_points under a Today label.
  const dailyScore = team?.daily_points || 0;
  const teamName = team?.name || 'No Team';
  const isGlobal = leagues.find(l => l.id === localLeague)?.league_type === 'global';
  const isCommissioner = leagues.find(l => l.id === localLeague)?.owner_id === user?.id;
  // v2026-06 — Tester sandbox badge: show only when an admin/tester is viewing
  // their private Sandbox Arena (real ₣1,000,000 starting budget).
  const isTesterAccount = user?.role === 'admin' || user?.is_test_account;
  const isSandboxView = isTesterAccount && localLeague === SANDBOX_LEAGUE_ID;
  
  // Net Worth = budget remaining + sum of current entity values on team
  const entityValues = (team?.slots || []).reduce((sum, slot) => {
    const price = slot.entity?.pricing?.price || slot.acquired_price || 0;
    return sum + price;
  }, 0);
  // v6.x — Net Worth = cash (remaining budget) + current value of holdings,
  // like cash + holdings in investing. Pre-draft / "No Team" in the selected
  // league: fall back to the league's starting budget so Net Worth matches
  // the Budget card instead of showing ₣0.00 while Budget shows ₣1,000.
  const _curLeague = leagues.find(l => l.id === localLeague);
  const _groups = splitLeagues(leagues);
  const _startingBudget = _curLeague?.settings?.starting_budget ?? _curLeague?.starting_budget ?? 1000;
  const _cash = team ? (team.budget_remaining || 0) : _startingBudget;
  // Quarterly mirror teams rank quarter-relative (minus the quarter-start offset).
  // A closed league shows the frozen figure its Final standings rank by.
  const netWorth = (_curLeague?.ended && typeof team?.net_worth === 'number')
    ? team.net_worth
    : _cash + entityValues - (team?.quarter_offset || 0);

  const copyCode = () => {
    if (leagueCode) {
      try { navigator.clipboard.writeText(leagueCode); } catch(e) {}
      toast.success('League code copied!');
    }
  };

  return (
    <div className="sticky top-0 z-40 glass-bg border-b border-border/60" data-testid="global-header">
      <div className="px-4 py-2">
        {/* Row 1: League selector + Team name (min-w-0 chain so the select
            shrinks+truncates on ≤390px phones instead of overflowing) */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-xs font-semibold text-muted-foreground shrink-0">League:</span>
            <Select value={localLeague} onValueChange={handleLeagueChange}>
              <SelectTrigger data-testid="global-header-league-select" className="min-w-0 sm:min-w-[140px] max-w-[280px] w-auto h-8 text-xs border-border/60 overflow-hidden [&>span]:truncate">
                <SelectValue placeholder="Select League" />
              </SelectTrigger>
              <SelectContent>
                {_groups.current.map(lg => (
                  <SelectItem key={lg.id} value={lg.id} className="text-xs" data-testid={`league-option-${lg.id}`}>{lg.name}</SelectItem>
                ))}
                {_groups.past.length > 0 && (
                  <>
                    <SelectSeparator />
                    <SelectGroup data-testid="league-picker-final-standings-group">
                      <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Final standings</SelectLabel>
                      {_groups.past.map(lg => (
                        <SelectItem key={lg.id} value={lg.id} className="text-xs text-muted-foreground" data-testid={`league-option-${lg.id}`}>{lg.name}</SelectItem>
                      ))}
                    </SelectGroup>
                  </>
                )}
              </SelectContent>
            </Select>
            {/* Build 4 — honest lobby size: finalized (leaderboard-visible)
                teams, not raw auto-enrolled membership. */}
            {_curLeague?.ended && (
              <span
                className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--accent-text))] bg-[hsl(var(--accent)/0.12)] border border-[hsl(var(--accent)/0.35)] px-2 py-0.5 rounded-full"
                data-testid="league-final-badge"
              >
                Final
              </span>
            )}
            {!_curLeague?.ended && typeof _curLeague?.competing_count === 'number' && (
              <span
                className="text-[9px] font-semibold text-muted-foreground bg-secondary/60 border border-border/60 px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0"
                title="Teams that finalized their roster and appear on this league's leaderboard"
                data-testid="league-competing-badge"
              >
                {_curLeague.competing_count} competing
              </span>
            )}
            {isSandboxView && (
              <span
                className="text-[9px] font-bold text-amber-900 dark:text-amber-200 bg-amber-300/40 dark:bg-amber-400/15 border border-amber-400/50 px-2 py-0.5 rounded-full whitespace-nowrap"
                title="You're in the private tester sandbox with a ₣1,000,000 budget to draft any entity."
                data-testid="tester-sandbox-badge"
              >
                Tester sandbox — ₣1,000,000
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-xs font-medium text-right" style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} data-testid="global-header-team-name">
              {teamName}
            </span>
            {isCommissioner && !isGlobal && (
              <span className="text-[7px] font-bold text-[hsl(var(--accent-text))] bg-[hsl(var(--accent)/0.15)] px-1.5 py-0.5 rounded-full whitespace-nowrap">Commissioner</span>
            )}
          </div>
        </div>

        {/* Row 2: Invite Code | Net Worth (centered) | Today */}
        <div className="grid grid-cols-3 items-center gap-2">
          {/* LEFT: Invite Code */}
          <div className="flex items-center gap-1 justify-start">
            <span className="text-[9px] font-semibold text-muted-foreground whitespace-nowrap">Invite&nbsp;Code:</span>
            {isGlobal || !leagueCode ? (
              <span className="text-[10px] text-muted-foreground px-2 py-1 rounded-full bg-secondary/50">N/A</span>
            ) : (
              <button
                onClick={copyCode}
                aria-label={`Copy league invite code ${leagueCode}`}
                className="flex items-center gap-1 rounded-full bg-secondary/70 px-2.5 py-1 hover:bg-secondary active:scale-[0.98] transition-all"
                title="Click to copy league invite code"
              >
                <span className="text-[10px] font-mono font-bold">{leagueCode}</span>
                <Copy className="h-2.5 w-2.5 text-muted-foreground" />
              </button>
            )}
          </div>

          {/* CENTER: Net Worth (always centered) */}
          <div className="relative flex flex-col items-center rounded-full bg-[hsl(var(--accent)/0.15)] px-4 py-1 mx-auto shrink-0" data-testid="global-header-total-fd">
            <RewardCelebration />
            <span className="text-[8px] text-muted-foreground uppercase tracking-wider whitespace-nowrap">Net Worth</span>
            <span className="text-xs font-bold tabular-nums text-[hsl(var(--accent-text))] whitespace-nowrap">
              {team ? (
                <AnimatedNumber
                  value={netWorth}
                  snapshotKey={`networth:${localLeague || 'global'}:${user?.id || 'anon'}`}
                  durationMs={1100}
                  flashColors={false}
                  format={(n) => formatFrancAdaptive(n)}
                  testid="global-header-networth-value"
                />
              ) : formatFrancAdaptive(netWorth)}
            </span>
          </div>

          {/* RIGHT: Today (right-aligned, expands leftward) — hidden (layout kept) on a finished season */}
          <div className={`flex flex-col items-center rounded-full bg-secondary/70 px-4 py-1 ml-auto shrink-0 ${_curLeague?.ended ? 'invisible' : ''}`} aria-hidden={_curLeague?.ended ? true : undefined} data-testid="global-header-daily-score" title="Points earned since midnight UTC — resets daily">
            <span className="text-[8px] text-muted-foreground uppercase tracking-wider whitespace-nowrap">Today</span>
            <span className="text-xs font-bold tabular-nums flex items-center gap-0.5 whitespace-nowrap">
              <TrendingUp className="h-2.5 w-2.5 text-[hsl(var(--chart-5))]" />
              {team ? (
                <AnimatedNumber
                  value={dailyScore}
                  snapshotKey={`today:${localLeague || 'global'}:${user?.id || 'anon'}`}
                  durationMs={1100}
                  flashColors={false}
                  format={(n) => formatFranc(n, { sign: true })}
                  testid="global-header-today-value"
                />
              ) : formatFranc(dailyScore, { sign: true })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
